import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { Box } from 'entities/Box';
import { WarehouseForBooking } from 'entities/Warehouse';
import React, { FC, memo, ReactNode, useCallback, useEffect } from 'react';
import { InfoTag } from 'shared/ui/InfoTag';
import { Trans } from 'react-i18next';
import { bookingHeaderActions, BookingSteps, getActiveStep, getCompletedSteps } from 'widgets/BookingHeader';
import { AppRoutes } from 'app/config/routerConfig/types';
import { BookingInfoStack } from 'widgets/BookingInfoStack';
import { SelectBoxSizeForm } from 'widgets/SelectBoxSizeForm';
import { SelectWarehouseMap } from 'widgets/SelectWarehouseMap';
import { UserDetailsForm } from 'widgets/UserDetailsForm';
import { PaymentForm } from 'widgets/PaymentForm';
import { Button } from 'shared/ui/Button';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { contractOptionsActions, getContractOptions, UnitType } from 'entities/Contract';
import { boxSizeFilterActions } from 'features/BoxSizeFilter';
import { getBoxInfo } from '../model/selectors/getBoxInfo';
import { getWarehouseInfo } from '../model/selectors/getWarehouseInfo';
import { bookingPageActions } from '../model/slice/bookingPageSlice';
import { ContractInformationForm } from 'widgets/ContractInformationForm';
import { useSetBookingDataFromQuery } from '../utils/hooks/useSetBookingDataFromQuery';
import { useBookingUnitViewMode } from '../utils/hooks/useBookingUnitViewMode';
import { getLoggedUserData } from 'entities/User';
import { setBookingDataToLocalStorage } from '../utils/helpers/setBookingDataToLocalStorage';
import { globalConfirmModalActions } from 'app/globalConfirmModal';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { STARTED_BOOKING_DATA_KEY } from '../utils/constants';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { PriceDetailsTable } from 'features/PriceDetailsTable';
import { GratitudeModalType, useOpenGratitudeModal, GratitudeModal } from 'features/GratitudeModal';
import { ReserveBox } from 'features/ReserveBox';
import { useNavigate } from 'react-router-dom';

export const BookingPage: FC = memo(() => {
  const { t } = useAppTranslation('booking');
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const bookingStep = useAppSelector(getActiveStep);
  const completedSteps = useAppSelector(getCompletedSteps);
  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const boxInfo = useAppSelector(getBoxInfo);
  const contractOptions = useAppSelector(getContractOptions);
  const loggedUser = useAppSelector(getLoggedUserData);

  const { scrollToTop } = useScrollToElement();
  const { openGratitudeModal } = useOpenGratitudeModal();

  useSetBookingDataFromQuery();

  const {
    viewMode: bookingUnitViewMode,
    setMapMode: setBookingUnitMapMode,
    setListMode: setBookingUnitListMode,
  } = useBookingUnitViewMode();

  const {
    parsedQuery: { warehouseId },
  } = useQueryString();

  const closeConfirmModal = useCallback((): void => {
    dispatch(globalConfirmModalActions.setGlobalConfirmModalData(null));

    localStorage.removeItem(STARTED_BOOKING_DATA_KEY);
  }, [dispatch]);

  const openConfirmModal = useCallback((): void => {
    dispatch(
      globalConfirmModalActions.setGlobalConfirmModalData({
        title: t('It looks like you started a booking!'),
        description: t('Would you like to pick up where you left off?'),
        onCancel: closeConfirmModal,
        onOk: () => {
          const storedQuery = localStorage.getItem(STARTED_BOOKING_DATA_KEY);
          if (storedQuery) {
            const newUrl = `${window.location.pathname}?${storedQuery}`;
            window.location.assign(newUrl);
          }
        },
      }),
    );
  }, [closeConfirmModal, dispatch, t]);

  useEffect(() => {
    const startedBookingData = localStorage.getItem(STARTED_BOOKING_DATA_KEY);

    if (startedBookingData && !warehouseInfo && !warehouseId) {
      openConfirmModal();
    }
  }, [bookingStep, completedSteps, openConfirmModal, warehouseId, warehouseInfo]);

  useEffect(() => {
    scrollToTop();
  }, [bookingStep, scrollToTop]);

  useEffect(() => {
    return () => {
      dispatch(bookingPageActions.resetAllBookingInfo());
      dispatch(bookingHeaderActions.resetAllBookingData());
    };
  }, [dispatch]);

  const selectWarehouse = useCallback(
    (warehouse: WarehouseForBooking): void => {
      const isNewWarehouseSelected = warehouseInfo?.warehouseId !== warehouse.warehouseId;

      if (isNewWarehouseSelected) {
        dispatch(
          contractOptionsActions.setContractOptions({
            insuranceId: null,
            insuranceAmountWithVat: 0,
            insuranceAmountWithoutVat: 0,
            insuranceVatRateAmount: 0,
            coverageAmount: null,
          }),
        );
        dispatch(bookingHeaderActions.clearBookingProgressData());
        dispatch(bookingPageActions.clearInfoAfterWarehouseChange());
        dispatch(boxSizeFilterActions.resetFilter());
      }

      dispatch(bookingPageActions.setWarehouseInfo(warehouse));
      dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.SELECT_UNIT));

      setBookingDataToLocalStorage({ warehouseId: warehouse.warehouseId, boxId: undefined, sizeCodeId: undefined }, navigate);
    },
    [dispatch, navigate, warehouseInfo?.warehouseId],
  );

  const selectBox = useCallback(
    (box: Box | undefined, sizeCodeId?: string): void => {
      if (!box) {
        dispatch(bookingPageActions.clearBoxInfo());
        setBookingDataToLocalStorage({ boxId: undefined, sizeCodeId: undefined }, navigate);

        return;
      }

      const isNewBoxSelected = boxInfo?.boxId !== box.boxId;

      if (isNewBoxSelected) {
        dispatch(
          contractOptionsActions.setContractOptions({
            promoCodeValue: null,
          }),
        );
      }

      dispatch(bookingPageActions.setBoxInfo({ selectedBox: box, selectedSizeCodeId: sizeCodeId }));

      setBookingDataToLocalStorage({ sizeCodeId, boxId: box.boxId }, navigate);
    },
    [boxInfo?.boxId, dispatch, navigate, warehouseInfo?.warehouseId],
  );

  const bookBox = useCallback(
    (box: Box, sizeCodeId?: string) => {
      const isNewBoxSelected = boxInfo?.boxId !== box.boxId;

      if (isNewBoxSelected) {
        dispatch(
          contractOptionsActions.setContractOptions({
            promoCodeValue: null,
          }),
        );
      }

      dispatch(bookingPageActions.setBoxInfo({ selectedBox: box, selectedSizeCodeId: sizeCodeId }));
      dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.CONTRACT_INFORMATION));

      setBookingDataToLocalStorage({ sizeCodeId, boxId: box.boxId }, navigate);
    },
    [boxInfo?.boxId, dispatch, navigate],
  );

  const completeContractInfoAuthorized = useCallback((): void => {
    dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.PAYMENT));
  }, [dispatch]);

  const completeContractInfoUnAuthorized = useCallback((): void => {
    dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.YOUR_DETAILS));
  }, [dispatch]);

  const onRentOptionsChange = useCallback(() => {
    dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.CONTRACT_INFORMATION));
  }, [dispatch]);

  const goToPaymentForm = useCallback(() => {
    dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.PAYMENT));
  }, [dispatch]);

  const bookAndPay = useCallback((): void => {
    localStorage.removeItem(STARTED_BOOKING_DATA_KEY);

    console.log('success booking');
  }, []);

  const handleOpenGratitudeModal = useCallback((): void => {
    openGratitudeModal({
      description: t(
        'Our company values each and every customer. If you have any questions or feedback, please don`t hesitate to reach out!',
      ),
      type: GratitudeModalType.RESERVATION,
      buttonId: 'reservation id', // TODO ADD ID FROM MARCUS !!!
    });
  }, [openGratitudeModal, t]);

  const onReservationSuccess = useCallback(() => {
    localStorage.removeItem(STARTED_BOOKING_DATA_KEY);

    navigate(AppRoutes.MY_BOXES);

    handleOpenGratitudeModal();
  }, [handleOpenGratitudeModal, navigate]);

  const renderStepScreen = useCallback((): Nullable<ReactNode> => {
    switch (bookingStep) {
      case BookingSteps.SELECT_LOCATION:
        return <SelectWarehouseMap onCompleteStep={selectWarehouse} />;
      case BookingSteps.SELECT_UNIT:
        return (
          <SelectBoxSizeForm
            selectedBoxId={boxInfo?.boxId}
            selectedWarehouseId={warehouseInfo?.warehouseId}
            onSelectBox={selectBox}
            onSelectWarehouse={selectWarehouse}
            onCompleteStep={bookBox}
            unitViewMode={bookingUnitViewMode}
            onSetUnitViewMap={setBookingUnitMapMode}
            onSetUnitViewList={setBookingUnitListMode}
            warehouseInfo={warehouseInfo}
            accountType={loggedUser?.accountType}
            isShowPromotionPriceNeeded={bookingStep === BookingSteps.SELECT_UNIT}
          />
        );
      case BookingSteps.CONTRACT_INFORMATION:
        return (
          <ContractInformationForm
            onCompleteStepAuthorized={completeContractInfoAuthorized}
            onCompleteStepUnAuthorized={completeContractInfoUnAuthorized}
            selectedWarehouseId={warehouseInfo?.warehouseId}
          />
        );
      case BookingSteps.YOUR_DETAILS:
        return (
          <UserDetailsForm
            warehouseInfo={warehouseInfo}
            boxInfo={boxInfo}
            contractInfo={contractOptions}
            onCompleteStep={goToPaymentForm}
            onRentOptionsChange={onRentOptionsChange}
          />
        );
      case BookingSteps.PAYMENT:
        if (boxInfo?.unitType === UnitType.OFFICE) {
          return (
            <div>
              <div className="bg-secondaryLight rounded-lg p-6">
                <PriceDetailsTable boxInfo={boxInfo} contractInfo={contractOptions} />

                <div className="flex flex-col mt-2">
                  <InfoTag className="w-full">{t('Administrator will contact you to provide instructions')}</InfoTag>
                  <ReserveBox
                    action={
                      <Button
                        containerClassName="w-full mt-2"
                        size="medium"
                        theme="primary"
                        description={
                          <Trans
                            t={t}
                            i18nKey="Reserve for <0>3 days free</0>. <1>No credit card needed!</1>"
                            components={[
                              <span key="0" className="text-accent text-xs font-medium" />,
                              <div key="1" className="text-xs font-normal" />,
                            ]}
                          />
                        }
                      >
                        {t('Reserve')}
                      </Button>
                    }
                    boxId={boxInfo?.boxId}
                    contractOptions={contractOptions}
                    warehouseId={warehouseInfo?.warehouseId}
                    onSuccess={onReservationSuccess}
                    userId={loggedUser?.userId}
                  />
                </div>
              </div>
            </div>
          );
        }

        return <PaymentForm boxInfo={boxInfo} warehouseInfo={warehouseInfo} contractInfo={contractOptions} onCompleteStep={bookAndPay} />;
      default:
        return null;
    }
  }, [
    bookingStep,
    loggedUser?.accountType,
    selectWarehouse,
    boxInfo,
    warehouseInfo,
    selectBox,
    bookBox,
    bookingUnitViewMode,
    setBookingUnitMapMode,
    setBookingUnitListMode,
    completeContractInfoAuthorized,
    completeContractInfoUnAuthorized,
    onRentOptionsChange,
    contractOptions,
    goToPaymentForm,
    bookAndPay,
  ]);

  return (
    <div className="mt-9 flex flex-col justify-between space-y-6 desktop:space-x-3 desktop:space-y-0 desktop:flex-row">
      <BookingInfoStack
        className="desktop:w-[425px] desktop:max-h-[calc(100vh-(theme(spacing.headerHeight)+34px+24px))] desktop:sticky desktop:top-[calc(theme(spacing.headerHeight)+34px)] desktop:shrink-0 desktop:!overflow-y-auto"
        bookingStep={bookingStep}
        completedSteps={completedSteps}
        warehouseInfo={warehouseInfo}
        boxInfo={boxInfo}
        contractInfo={contractOptions}
        accountType={loggedUser?.accountType}
      />
      <div className="desktop:basis-3/4 desktop:flex-1">{renderStepScreen()}</div>
      <GratitudeModal />
    </div>
  );
});
